// Sound utility for playing confirmation/notification sounds
// Uses Web Audio API for reliable, instant playback

class SoundPlayer {
  constructor() {
    this.audioContext = null;
    this.initialized = false;
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (this.initialized) return;
    
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio API not supported:', e);
    }
  }

  // Play a success/confirmation sound (pleasant chime)
  playSuccess() {
    this.init();
    if (!this.audioContext) return;

    const now = this.audioContext.currentTime;
    
    // Create a pleasant two-tone chime
    const frequencies = [523.25, 659.25]; // C5 and E5 (major third - pleasant sound)
    
    frequencies.forEach((freq, index) => {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.value = freq;
      oscillator.type = 'sine';
      
      // Stagger the notes slightly for a chime effect
      const startTime = now + (index * 0.08);
      
      // Volume envelope (fade in quickly, sustain, fade out)
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, startTime + 0.4);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + 0.5);
    });
  }

  // Play an error sound (lower, shorter)
  playError() {
    this.init();
    if (!this.audioContext) return;

    const now = this.audioContext.currentTime;
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.value = 220; // A3 - lower tone for error
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.3, now + 0.01);
    gainNode.gain.linearRampToValueAtTime(0, now + 0.15);
    
    oscillator.start(now);
    oscillator.stop(now + 0.2);
  }

  // Play a notification sound (single pleasant tone)
  playNotification() {
    this.init();
    if (!this.audioContext) return;

    const now = this.audioContext.currentTime;
    
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    
    oscillator.frequency.value = 880; // A5
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(0.2, now + 0.02);
    gainNode.gain.linearRampToValueAtTime(0, now + 0.3);
    
    oscillator.start(now);
    oscillator.stop(now + 0.35);
  }
}

// Export singleton instance
export const soundPlayer = new SoundPlayer();

// Convenience functions
export const playSuccessSound = () => soundPlayer.playSuccess();
export const playErrorSound = () => soundPlayer.playError();
export const playNotificationSound = () => soundPlayer.playNotification();
